import { Link } from 'react-router';
import type { OrderSummary } from '../../data/orders.ts';
import { formatIsoDate } from '../../domain/dates.ts';
import { equipmentLabel, WORK_TYPE_LABELS } from '../../domain/orders.ts';
import { StatusBadge } from '../../ui/StatusBadge.tsx';
import { AccountIcon, CalendarIcon, WorkTypeIcon } from '../../ui/icons.tsx';

interface OrderCardProps {
  order: OrderSummary;
  /** Personal administrativo: muestra el técnico (y el Tipo Garantía al pasar el cursor); el técnico ve el Tipo Garantía. */
  staff: boolean;
  /** En la ficha del cliente el nombre sobra: no se muestra. */
  hideCustomer?: boolean;
}

// Tarjeta de la lista de órdenes (panel y ficha del cliente): cliente, equipo en negrita y falla reportada.
export function OrderCard({ order, staff, hideCustomer = false }: OrderCardProps) {
  return (
    <Link to={`/orders/${order.id}`} className={`order-card order-card-${order.status}`}>
      <div className="order-card-top">
        <strong>
          OS <span className="folio-number">{order.folio}</span>
        </strong>{' '}
        <span className="order-card-ot">OT {order.order_number}</span>
        {order.has_quote ? <QuoteBadge className="order-card-ot" /> : null}
        <StatusBadge status={order.status} />
      </div>
      {hideCustomer ? null : <span className="muted">{order.customer_name}</span>}
      <span className="order-card-customer">{equipmentLabel(order)}</span>
      <span className="muted">{order.reported_fault}</span>
      <span className="order-card-meta">
        <span>
          <CalendarIcon />
          Solicitud {formatIsoDate(order.request_date)}
        </span>
        {staff ? (
          // Personal: el técnico asignado; al pasar el cursor, el Tipo Garantía (como lo ve el técnico).
          <span className="order-card-swap">
            <Assignee name={order.assigned_name} className="order-card-assignee" />
            <WorkType type={order.work_type} className="order-card-work-type" />
          </span>
        ) : (
          <WorkType type={order.work_type} />
        )}
      </span>
    </Link>
  );
}

// La orden tiene presupuesto: píldora junto a la OT (tarjeta y tabla). El lector de pantalla lee la palabra completa.
export function QuoteBadge({ className }: { className: string }) {
  return (
    <span className={`${className} order-quote-badge`} title="Tiene presupuesto">
      <span aria-hidden="true">PRESUP.</span>
      <span className="visually-hidden">Tiene presupuesto</span>
    </span>
  );
}

// Sin técnico asignado muestra "Back Office" en rojo: la orden necesita que alguien la asigne. También en la tabla.
export function Assignee({ name, className }: { name: string | null; className?: string }) {
  const classes = [className, name ? null : 'unassigned'].filter(Boolean).join(' ');
  return (
    <span className={classes || undefined}>
      <AccountIcon />
      {name ?? 'Back Office'}
    </span>
  );
}

export function WorkType({ type, className }: { type: OrderSummary['work_type']; className?: string }) {
  return (
    <span className={className}>
      <WorkTypeIcon type={type} />
      {WORK_TYPE_LABELS[type]}
    </span>
  );
}
